'use client';

import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';

interface TrialBalanceItem {
  account_id: number;
  account_code: string;
  account_name: string;
  account_type: string;
  debit: number;
  credit: number;
}

interface Movement {
  id: number;
  date: string;
  description: string;
  debit: number;
  credit: number;
  running_balance: number;
}

interface LedgerData {
  account_id: number;
  account_code: string;
  account_name: string;
  account_type: string;
  total_debit: number;
  total_credit: number;
  balance: number;
  movements: Movement[];
}

export default function GeneralLedger() {
  const [activeTab, setActiveTab] = useState<'trial' | 'ledger' | 'account'>('trial');
  const [trialBalance, setTrialBalance] = useState<TrialBalanceItem[]>([]);
  const [ledgerData, setLedgerData] = useState<any[]>([]);
  const [accountData, setAccountData] = useState<LedgerData | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadTrialBalance();
  }, []);

  const loadTrialBalance = async () => {
    try {
      const response: any = await api.get('/comptabilitat/ledger/trial-balance', {
        params: { as_of_date: asOfDate },
      });
      if (response.success) {
        setTrialBalance(response.data?.data || []);
      }
    } catch (error) {
      console.error('Error carregant balance:', error);
    }
  };

  const loadGeneralLedger = async () => {
    try {
      const response: any = await api.get('/comptabilitat/ledger/general-ledger', {
        params: { as_of_date: asOfDate },
      });
      if (response.success) {
        setLedgerData(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant major:', error);
    }
  };

  const loadAccountBalance = async (accountCode: string) => {
    if (!accountCode) return;
    try {
      const response: any = await api.get(`/comptabilitat/ledger/account/by-code/${accountCode}`, {
        params: { as_of_date: asOfDate },
      });
      if (response.success) {
        setAccountData(response.data);
      }
    } catch (error) {
      console.error('Error carregant compte:', error);
    }
  };

  const handleTabChange = (tab: 'trial' | 'ledger' | 'account') => {
    setActiveTab(tab);
    if (tab === 'trial') loadTrialBalance();
    else if (tab === 'ledger') loadGeneralLedger();
  };

  const totals = trialBalance.reduce(
    (acc, item) => ({
      debit: acc.debit + item.debit,
      credit: acc.credit + item.credit,
    }),
    { debit: 0, credit: 0 }
  );

  return (
    <div className="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Major Comptable</h1>
        <div className="flex gap-2">
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-40"
          />
          <Button onClick={() => handleTabChange(activeTab)}>Actualitzar</Button>
        </div>
      </div>

      <div className="flex gap-4 border-b">
        <Button
          variant={activeTab === 'trial' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('trial')}
        >
          Balanç de Comprovació
        </Button>
        <Button
          variant={activeTab === 'ledger' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('ledger')}
        >
          Llibre Major
        </Button>
        <Button
          variant={activeTab === 'account' ? 'default' : 'ghost'}
          onClick={() => handleTabChange('account')}
        >
          Per Compte
        </Button>
      </div>

      {activeTab === 'trial' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b grid grid-cols-5 gap-4 text-[12px] font-medium text-muted-foreground">
            <div>Codi</div>
            <div>Compte</div>
            <div>Tipus</div>
            <div className="text-right">Deure</div>
            <div className="text-right">Haver</div>
          </div>
          {trialBalance.length > 0 ? (
            <>
              {trialBalance.map((item) => (
                <div key={item.account_id} className="p-4 border-b grid grid-cols-5 gap-4">
                  <div className="font-mono text-sm">{item.account_code}</div>
                  <div>{item.account_name.substring(0, 40)}</div>
                  <div className="text-xs text-gray-600">{item.account_type}</div>
                  <div className="text-right font-mono">{item.debit.toFixed(2)}</div>
                  <div className="text-right font-mono">{item.credit.toFixed(2)}</div>
                </div>
              ))}
              <div className="p-4 bg-gray-50 grid grid-cols-5 gap-4 font-bold border-t-2">
                <div className="col-span-3">TOTALS:</div>
                <div className="text-right">{totals.debit.toFixed(2)}</div>
                <div className="text-right">{totals.credit.toFixed(2)}</div>
              </div>
              <div className={`p-4 text-center font-bold ${Math.abs(totals.debit - totals.credit) < 0.01 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {Math.abs(totals.debit - totals.credit) < 0.01 ? '✓ Balancejat' : '✗ No balancejat'}
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No hi ha dades
            </div>
          )}
        </Card>
      )}

      {activeTab === 'ledger' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b grid grid-cols-6 gap-2 text-[12px] font-medium text-muted-foreground">
            <div>Data</div>
            <div>Codi</div>
            <div>Descripció</div>
            <div className="text-right">Deure</div>
            <div className="text-right">Haver</div>
            <div className="text-right">Saldo</div>
          </div>
          {ledgerData.length > 0 ? (
            ledgerData.map((item, idx) => (
              <div key={idx} className="p-3 border-b grid grid-cols-6 gap-2 text-sm hover:bg-gray-50">
                <div>{new Date(item.date).toLocaleDateString('ca-ES')}</div>
                <div className="font-mono text-xs">{item.account?.code}</div>
                <div className="truncate">{item.description}</div>
                <div className="text-right font-mono">{item.debit.toFixed(2)}</div>
                <div className="text-right font-mono">{item.credit.toFixed(2)}</div>
                <div className="text-right font-mono">
                  {item.running_balance?.toFixed(2) || '0.00'}
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              No hi ha moviments
            </div>
          )}
        </Card>
      )}

      {activeTab === 'account' && (
        <div className="flex flex-col gap-4">
          <Card className="p-4">
            <label className="text-sm font-medium">Selecciona Compte:</label>
            <div className="flex gap-2 mt-2">
              <Input
                type="text"
                placeholder="Codi de compte (ex: 621)"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              />
              <Button onClick={() => loadAccountBalance(selectedAccountId)}>
                Consultar
              </Button>
            </div>
          </Card>

          {accountData && (
            <Card className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <div className="text-sm text-gray-600">Compte</div>
                  <div className="font-bold text-sm">
                    {accountData.account_code} - {accountData.account_name}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Tipus</div>
                  <div className="font-bold">{accountData.account_type}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6 bg-gray-50 p-4 rounded">
                <div>
                  <div className="text-sm text-gray-600">Total Deure</div>
                  <div className="font-bold text-sm">{accountData.total_debit.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Total Haver</div>
                  <div className="font-bold text-sm">{accountData.total_credit.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Saldo</div>
                  <div className={`font-bold text-sm ${accountData.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {accountData.balance.toFixed(2)}
                  </div>
                </div>
              </div>

              <h3 className="font-bold mb-3">Moviments:</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Descripció</th>
                      <th className="text-right p-2">Deure</th>
                      <th className="text-right p-2">Haver</th>
                      <th className="text-right p-2">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountData.movements.map((mov, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {new Date(mov.date).toLocaleDateString('ca-ES')}
                        </td>
                        <td className="p-2 truncate">{mov.description}</td>
                        <td className="p-2 text-right font-mono">
                          {mov.debit.toFixed(2)}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {mov.credit.toFixed(2)}
                        </td>
                        <td className="p-2 text-right font-mono font-bold">
                          {mov.running_balance.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
